import CashSession from 'src/models/cash/CashSession'
import CashTray from 'src/models/cash/CashTray'
import Model, { encoders, ID, Integer, IsoDateTime } from 'src/models/model'
import Site from 'src/models/site/Site'
import User from 'src/models/user/User'

export const INTERVALS = {
  DAILY: 'daily',
  WEEKLY: 'weekly'
}
INTERVALS.ALL = _.values(INTERVALS)

export const TYPES = {
  CASH_TRAY: 'cash-tray',
  USER: 'user'
}
TYPES.ALL = _.values(TYPES)

export default class CashCount extends Model {
  static name = 'CashCount'

  static versioned = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.PointOfSale, Model.permissionCategories.HardwareAndRegisterStation]

  static define () {
    this.fields = {
      id: {
        type: ID
      },

      site: {
        type: Site,
        reference: true,
        encode: encoders.hasOne,
        default: null,
        output: true
      },

      type: {
        type: String,
        output: true,
        default: null
      },

      cashTray: {
        type: CashTray,
        reference: true,
        encode: encoders.hasOne,
        default: null
      },
      user: {
        type: User,
        encode: encoders.hasOne,
        default: null,
        output: true
      },

      hundredthCount: {
        type: Integer
      },
      fiftiethCount: {
        type: Integer
      },
      twentiethCount: {
        type: Integer
      },
      tenthCount: {
        type: Integer
      },
      fifthCount: {
        type: Integer
      },
      quarterCount: {
        type: Integer
      },
      halfCount: {
        type: Integer
      },
      oneCount: {
        type: Integer
      },
      twoCount: {
        type: Integer
      },
      fiveCount: {
        type: Integer
      },
      tenCount: {
        type: Integer
      },
      twentyCount: {
        type: Integer
      },
      fiftyCount: {
        type: Integer
      },
      hundredCount: {
        type: Integer
      },

      bank: {
        type: Number,
        default: 0,
        encode: encoders.cents
      },
      check: {
        type: Number,
        default: 0,
        encode: encoders.cents
      },
      other: {
        type: Number,
        default: 0,
        encode: encoders.cents
      },

      sales: {
        type: Number,
        default: 0,
        output: true
      },
      saleCount: {
        type: Integer,
        output: true
      },
      refunds: {
        type: Number,
        default: 0,
        output: true
      },
      refundCount: {
        type: Integer,
        output: true
      },
      adjustments: {
        type: Number,
        default: 0,
        output: true
      },
      adjustmentCount: {
        type: Integer,
        output: true
      },
      notes: {
        type: String,
        output: true
      },
      creditCardSales: {
        type: Number,
        default: 0,
        output: true
      },
      creditCardSaleCount: {
        type: Integer,
        output: true
      },
      creditCardRefunds: {
        type: Number,
        default: 0,
        output: true
      },
      creditCardRefundCount: {
        type: Integer,
        output: true
      },

      countedAt: {
        type: IsoDateTime,
        output: true,
        default: null
      },
      final: {
        type: Boolean,
        output: true,
        default: false
      },

      lastCashSession: {
        type: CashSession,
        default: null,
        encode: encoders.hasOne,
        output: true
      },

      action: {
        type: String,
        input: true,
        default: null
      }
    }

    this.computed = {
      counted () {
        return !!this.countedAt
      },
      cashCount () {
        const cents =
          (this.hundredthCount || 0) * 1 +
          (this.fiftiethCount || 0) * 2 +
          (this.twentiethCount || 0) * 5 +
          (this.tenthCount || 0) * 10 +
          (this.fifthCount || 0) * 20 +
          (this.quarterCount || 0) * 25 +
          (this.halfCount || 0) * 50 +
          (this.oneCount || 0) * 100 +
          (this.twoCount || 0) * 200 +
          (this.fiveCount || 0) * 500 +
          (this.tenCount || 0) * 1000 +
          (this.twentyCount || 0) * 2000 +
          (this.fiftyCount || 0) * 5000 +
          (this.hundredCount || 0) * 10000 +
          ((this.check || 0) * 100) +
          ((this.other || 0) * 100) -
          ((this.bank || 0) * 100)

        return cents / 100
      },
      net () {
        return this.sales - this.refunds + this.adjustments
      },
      overUnder () {
        return this.cashCount - this.net
      },
      icon () {
        if (this.final) return { name: 'check', color: 'positive' }

        return { name: 'remove', color: 'negative' }
      }
    }
  }

  async print () {
    this.action = 'print'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async finalize () {
    this.action = 'finalize'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
